import React from "react";
import { Flex, theme, Typography } from "antd";
import { useTranslate } from "@refinedev/core";
import { SparkleIcon } from "@phosphor-icons/react";
import { Button, Modal, Stack } from "@libertify/ui";
import { DocumentUploadArea } from "./DocumentUploadArea";
import { StepEyebrow } from "./ContentSidebar.styles";
import { ChooseAnotherLink } from "./CreateBlockedModal.styles";

interface CreateBlockedModalProps {
  open: boolean;
  /** Also used by "Choose another playbook" — just closes the modal. */
  onClose: () => void;
  playbookName: string;
  playbookDescription?: string;
  /** True once there's at least one visible document. */
  ready: boolean;
  isCreating: boolean;
  onCreate: () => void;
}

/**
 * Shown when "Create now" is clicked while blocked (no document yet). Reuses
 * the Step 1 upload area so adding a file here is identical to adding it in
 * the sidebar — same draft, same behavior. "Create now" here starts the same
 * creation flow as the main bar, once a document has been added.
 */
export const CreateBlockedModal: React.FC<CreateBlockedModalProps> = ({
  open,
  onClose,
  playbookName,
  playbookDescription,
  ready,
  isCreating,
  onCreate,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="medium"
      title={
        <Stack direction="vertical" gap={0}>
          <StepEyebrow $token={token}>
            {t("createExperience.createBlockedModal.eyebrow")}
          </StepEyebrow>
          <Typography.Title level={4} style={{ margin: "0 0 4px" }}>
            {playbookName}
          </Typography.Title>
          {playbookDescription && (
            <Typography.Text type="secondary">
              {playbookDescription}
            </Typography.Text>
          )}
        </Stack>
      }
      footer={
        <Flex gap={8} justify="center">
          <ChooseAnotherLink $token={token} type="button" onClick={onClose}>
            {t("createExperience.createBlockedModal.chooseAnother")}
          </ChooseAnotherLink>
          <Button
            variant="primary"
            size="m"
            leftIcon={<SparkleIcon size={16} weight="fill" />}
            loading={isCreating}
            disabled={!ready || isCreating}
            onClick={onCreate}
          >
            {t("createExperience.createBar.create")}
          </Button>
        </Flex>
      }
      styles={{
        content: {
          padding: 0,
        },
      }}
    >
      <DocumentUploadArea />
    </Modal>
  );
};
